import type { VoucherApplyResult, VoucherType } from '../../domain/vouchers/types'
import type { ApplyVoucherPaymentRequest, ApplyVoucherPaymentResponse, GetVoucherPaymentsBalanceRequest, GetVoucherPaymentsBalanceResponse } from './types'
import defaultFetch from 'isomorphic-unfetch'
import { mapVoucherApiToDomain, mapVoucherApplyResponseApiToDomain } from './voucherMapper'

interface Dependencies {
  env: { NEXT_PUBLIC_API_BASE_URL: string }
  fetch: typeof defaultFetch
  contextItemId: string
  groupUserId: string | null
}

export function createVoucherApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = { NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL },
    fetch = defaultFetch,
    contextItemId = '',
    groupUserId = null,
  } = dependencies as Dependencies

  async function getVoucherPaymentsBalance(body: Omit<GetVoucherPaymentsBalanceRequest, 'contextItemId'>): Promise<{ voucher: VoucherType, isValid: boolean }> {
    const response = await fetch(`${env.NEXT_PUBLIC_API_BASE_URL}/PaymentApi/GetVoucherPaymentsBalance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, contextItemId }),
    })
    if (!response.ok)
      throw new Error(`Failed to fetch voucher balance: ${response.status} ${response.statusText}`)
    const data = (await response.json()) as GetVoucherPaymentsBalanceResponse
    return {
      voucher: mapVoucherApiToDomain(data),
      isValid: data.isValid,
    }
  }

  async function applyVoucherPayment(body: Omit<ApplyVoucherPaymentRequest, 'contextItemId'>): Promise<{ result: VoucherApplyResult, isWellApplied: boolean }> {
    const response = await fetch(`${env.NEXT_PUBLIC_API_BASE_URL}/PaymentApi/ApplyVoucherPayment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, contextItemId, groupUserId }),
    })
    if (!response.ok)
      throw new Error(`Failed to apply voucher payment: ${response.status} ${response.statusText}`)
    const data = (await response.json()) as ApplyVoucherPaymentResponse
    return {
      result: mapVoucherApplyResponseApiToDomain(data),
      isWellApplied: data.isWellApplied,
    }
  }

  return { getVoucherPaymentsBalance, applyVoucherPayment }
}
